(() => {
  const selectors = {
    root: '[data-ctr-best-sellers]',
    tab: '.ctr-best-sellers__tab',
    panel: '.ctr-best-sellers__panel',
    controls: '[data-controls]',
    parkedPanel: 'template[data-ctr-panel]',
    prevBtn: '[data-prev]',
    nextBtn: '[data-next]',
  };

  /**
   * Custom element for each carousel panel.
   *
   * R80: this was a Swiper. `new Swiper(swiperEl, this.getConfig())` measured every slide
   * synchronously inside the tab-click handler and cost 386 of that interaction's 387 ms,
   * with 349-367 ms of it forced style-and-layout across five matched runs. R17 moved that
   * work off parse and R45 moved it off load; neither moved it out of the interaction,
   * because the interaction was the only place it had ever been.
   *
   * The row is now a CSS scroll-snap container. The browser lays it out. Nothing here reads
   * geometry: the current index comes from IntersectionObserver membership, and navigation
   * scrolls a known adjacent slide into view rather than measuring the set (Codex round 17).
   *
   * Deliberately dropped with Swiper: loop, autoplay and the pagination dots. Loop and
   * autoplay were both `false` on the live section, verified in the DOM, and their schema
   * settings are gone so nothing silently does nothing. Pagination removal was Charlie's call.
   */
  class CtrBestSellersCarousel extends HTMLElement {
    connectedCallback() {
      if (this._bound) return;

      this.track = this.querySelector('[data-track]');
      if (!this.track) return;

      this._bound = true;
      this.slides = Array.from(this.track.children);
      this.index = 0;
      this._visible = new Set();

      this.bindNavigation();
      this.observeSlides();
      this.updateControls();
    }

    disconnectedCallback() {
      if (this._observer) {
        this._observer.disconnect();
        this._observer = null;
      }
      this._bound = false;
    }

    /**
     * Track which slides are on screen. This is the primary source of position state --
     * no offsetLeft, no getBoundingClientRect, nothing that forces layout.
     *
     * Where IntersectionObserver is unavailable, updateControls() and scrollToIndex()
     * fall back to a counter instead (Codex round 18 finding 2). Returning here without
     * one left `index` pinned at 0 for the life of the page: Prev was permanently
     * disabled and every Next click re-targeted slide 1.
     */
    observeSlides() {
      if (!('IntersectionObserver' in window)) return;

      this._observer = new IntersectionObserver(
        (entries) => {
          for (const entry of entries) {
            if (entry.isIntersecting) this._visible.add(entry.target);
            else this._visible.delete(entry.target);
          }
          this.updateControls();
        },
        { root: this.track, threshold: 0.6 }
      );

      this.slides.forEach((slide) => this._observer.observe(slide));
    }

    /** Arrow boundaries and controls visibility, derived from observer state where there is one. */
    updateControls() {
      // No observer: drive the arrows from the counter scrollToIndex() maintains. Coarser --
      // it cannot know how many slides fit, so it steps one at a time and leaves the controls
      // bar visible rather than guessing -- but it reads no geometry either.
      if (!this._observer) {
        if (this.prevBtn) this.prevBtn.disabled = this.index <= 0;
        if (this.nextBtn) this.nextBtn.disabled = this.index >= this.slides.length - 1;
        return;
      }

      // filtering this.slides (not the Set) keeps DOM order
      const visible = this.slides.filter((slide) => this._visible.has(slide));
      const firstIndex = visible.length ? this.slides.indexOf(visible[0]) : 0;
      const lastIndex = visible.length
        ? this.slides.indexOf(visible[visible.length - 1])
        : 0;

      this.index = firstIndex;

      if (this.prevBtn) this.prevBtn.disabled = firstIndex <= 0;
      if (this.nextBtn) this.nextBtn.disabled = lastIndex >= this.slides.length - 1;

      const controls = this.querySelector(selectors.controls);
      if (controls) {
        // everything already fits, so there is nothing to navigate to
        controls.classList.toggle('is-hidden', visible.length >= this.slides.length);
      }
    }

    /**
     * Scroll a known adjacent slide into view. `inline: 'start'` is the logical inline axis,
     * so the scroll itself needs no direction handling; the arrow GLYPHS are mirrored in
     * assets/ctr-best-sellers.css under [dir="rtl"], because a flipped chevron is not a
     * logical property (Codex round 18 finding 3).
     */
    scrollToIndex(target) {
      const clamped = Math.max(0, Math.min(target, this.slides.length - 1));
      const slide = this.slides[clamped];
      if (!slide) return;

      // The observer would otherwise be the only writer of this.index.
      if (!this._observer) {
        this.index = clamped;
        this.updateControls();
      }

      const reduceMotion =
        typeof matchMedia === 'function' &&
        matchMedia('(prefers-reduced-motion: reduce)').matches;

      slide.scrollIntoView({
        behavior: reduceMotion ? 'auto' : 'smooth',
        inline: 'start',
        block: 'nearest',
      });
    }

    bindNavigation() {
      if (this._navBound) return;
      this._navBound = true;

      this.prevBtn = this.querySelector(selectors.prevBtn);
      this.nextBtn = this.querySelector(selectors.nextBtn);

      if (this.prevBtn) {
        this.prevBtn.addEventListener('click', () => this.scrollToIndex(this.index - 1));
      }
      if (this.nextBtn) {
        this.nextBtn.addEventListener('click', () => this.scrollToIndex(this.index + 1));
      }
    }
  }

  if (!customElements.get('ctr-best-sellers-carousel')) {
    customElements.define('ctr-best-sellers-carousel', CtrBestSellersCarousel);
  }

  /**
   * Move a parked panel's markup out of its <template> and into the document.
   * No-op once done, and a no-op for the first panel, which was never parked.
   */
  const hydratePanel = (panel) => {
    const parked = panel.querySelector(selectors.parkedPanel);
    if (!parked) return;
    parked.replaceWith(parked.content);
  };

  /** Tab switching */
  const initBestSellers = (container) => {
    if (!container || container.dataset.ctrBestSellersReady === 'true') return;

    const tabs = Array.from(container.querySelectorAll(selectors.tab));
    const panels = Array.from(container.querySelectorAll(selectors.panel));

    if (!tabs.length || !panels.length) return;

    const setActiveTab = (index, { moveFocus = false } = {}) => {
      tabs.forEach((tab, tabIndex) => {
        const isActive = tabIndex === index;
        tab.classList.toggle('is-active', isActive);
        tab.setAttribute('aria-selected', isActive ? 'true' : 'false');

        // A11y: roving tabindex. With role="tab" the tablist is ONE tab stop, and only the
        // selected tab is reachable with Tab; the rest are reached with the arrow keys below.
        // Do not focus on init or on click -- click already moved focus, and init would steal it.
        tab.tabIndex = isActive ? 0 : -1;
      });

      if (moveFocus && tabs[index]) tabs[index].focus();

      panels.forEach((panel, panelIndex) => {
        const isActive = panelIndex === index;

        // PERF (Claude R27): every panel but the first ships inside a <template>, so
        // its ~136 nodes and its add-to-cart forms are not in the document until the
        // tab is opened.
        if (isActive) hydratePanel(panel);

        panel.classList.toggle('is-active', isActive);
        panel.hidden = !isActive;

        // R80: nothing to construct, so the hydrate-then-unhide ORDER no longer carries any
        // weight. It used to: connectedCallback bailed on a hidden check and a lazy-init
        // branch built the Swiper later. Both are gone (Codex round 18 finding 4). Hydration
        // now upgrades the custom element while the panel is still hidden, and its
        // connectedCallback only binds two click listeners and registers an
        // IntersectionObserver -- neither reads geometry, so neither cares that the panel has
        // no boxes yet. The observer delivers its first entries once the panel is unhidden,
        // and that callback is what gives updateControls() real position state.
        // `userInitiated` is gone from the signature: it existed only to choose "init now"
        // over "init later", and there is no init.
      });
    };

    tabs.forEach((tab, tabIndex) => {
      tab.addEventListener('click', () => {
        setActiveTab(Number(tab.dataset.tabIndex || 0));
      });

      // A11y: the ARIA tabs pattern moves selection with the arrow keys, plus Home/End.
      // Wraps at both ends. No geometry is read, so this costs nothing on the R80 budget.
      tab.addEventListener('keydown', (event) => {
        const last = tabs.length - 1;
        let next = null;

        if (event.key === 'ArrowRight' || event.key === 'ArrowDown') next = tabIndex === last ? 0 : tabIndex + 1;
        else if (event.key === 'ArrowLeft' || event.key === 'ArrowUp') next = tabIndex === 0 ? last : tabIndex - 1;
        else if (event.key === 'Home') next = 0;
        else if (event.key === 'End') next = last;

        if (next === null) return;
        event.preventDefault();
        setActiveTab(next, { moveFocus: true });
      });
    });

    container.dataset.ctrBestSellersReady = 'true';

    const initialIndex = tabs.findIndex((tab) => tab.classList.contains('is-active'));
    setActiveTab(initialIndex >= 0 ? initialIndex : 0);
  };

  const initAll = (scope = document) => {
    scope.querySelectorAll(selectors.root).forEach(initBestSellers);
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initAll(), { once: true });
  } else {
    initAll();
  }

  // Shopify theme editor support
  document.addEventListener('shopify:section:load', (event) => initAll(event.target));
})();
